import { renderLoadingError } from "@single-js/common/components/render-loading-error/render-loading-error";
import { FakeLinkCell } from "@single-js/common/components/super-table/super-table";
import NumberFormatter from "@single-js/common/util/number-formatter";
import { LONG_EM_DASH } from "@single-js/common/util/symbols";
import { formatHostMemory, getHostDiskUsage, getHostMemoryUsage, } from "data/models";
import * as React from "react";
import NodeBadge from "view/common/node-badge";
import { IconTip } from "view/common/overrides/icon-tip";
import { RatioMetricCell } from "view/common/ratio-metric-cell";
import "./columns-info.scss";
const HOSTS_ADDRESS_COLUMN = {
    id: "hostAddress",
    title: "Host Address",
    formatter: (host) => (React.createElement(FakeLinkCell, null, host.address)),
    getValue: (host) => host.address,
};
const HOSTS_NODES_COLUMN = {
    id: "hostNodes",
    title: "Nodes",
    formatter: (host) => {
        let numLeaves = 0;
        let numChildAggs = 0;
        let numMasterAggs = 0;
        for (let i = 0; i < host.nodes.length; i++) {
            if (host.nodes[i].role === "LEAF") {
                numLeaves++;
            }
            else if (host.nodes[i].role === "AGGREGATOR") {
                numChildAggs++;
            }
            else if (host.nodes[i].role === "MASTER_AGGREGATOR") {
                numMasterAggs++;
            }
        }
        let leavesNode;
        let childAggsNode;
        let masterAggsNode;
        if (numLeaves > 0) {
            leavesNode = (React.createElement(NodeBadge, { count: numLeaves, role: "LEAF", classname: "badge" }));
        }
        if (numChildAggs > 0) {
            childAggsNode = (React.createElement(NodeBadge, { count: numChildAggs, role: "AGGREGATOR", classname: "badge" }));
        }
        if (numMasterAggs > 0) {
            masterAggsNode = (React.createElement(NodeBadge, { count: numMasterAggs, role: "MASTER_AGGREGATOR", classname: "badge" }));
        }
        return (React.createElement("div", { className: "node-badges-cell" },
            masterAggsNode,
            childAggsNode,
            leavesNode));
    },
    getValue: (host) => {
        let maxNode = -1;
        for (let i = 0; i < host.nodes.length; i++) {
            if (host.nodes[i].role === "MASTER_AGGREGATOR") {
                return 2;
            }
            else if (host.nodes[i].role === "AGGREGATOR" && maxNode < 1) {
                maxNode = 1;
            }
            else if (host.nodes[i].role === "LEAF" && maxNode < 0) {
                maxNode = 0;
            }
        }
        return maxNode;
    },
};
const HOSTS_CPU_COUNT_COLUMN = {
    id: "hostCpuCount",
    title: "CPU Cores",
    formatter: (host) => host.cpuCount ? host.cpuCount.toNumber() : LONG_EM_DASH,
    getValue: (host) => {
        if (host.cpuCount === undefined) {
            return Number.NEGATIVE_INFINITY;
        }
        else {
            return host.cpuCount.toNumber(); // PLAT-3189
        }
    },
};
const HOSTS_CPU_USAGE_COLUMN = {
    id: "hostTotalCpuUsage",
    title: "Host CPU Usage",
    sort: "DISABLED",
    formatter: (host) => {
        const renderHostCpuUsage = (cpuUsage) => (React.createElement(RatioMetricCell, { labelPosition: "top", value: cpuUsage }));
        return renderLoadingError(host.liveMonitoring.totalCpuUsagePercentRate, renderHostCpuUsage, { cell: true });
    },
    getValue: (host) => {
        // If loading, return undefined. If error, return null.
        if (host.liveMonitoring.totalCpuUsagePercentRate.isSuccess()) {
            return host.liveMonitoring.totalCpuUsagePercentRate.value;
        }
        else if (host.liveMonitoring.totalCpuUsagePercentRate.isLoading()) {
            return undefined;
        }
        else {
            return null;
        }
    },
};
const HOSTS_MEMORY_COLUMN = {
    id: "hostMemory",
    title: "System Memory",
    formatter: formatHostMemory,
    getValue: (host) => {
        if (host.systemMemoryB === undefined) {
            return Number.NEGATIVE_INFINITY;
        }
        else {
            return host.systemMemoryB.toNumber(); // PLAT-3189
        }
    },
};
const HOSTS_MEMORY_USAGE_COLUMN = {
    id: "hostSystemMemoryUsage",
    title: (React.createElement("div", null,
        "Host Memory Usage",
        React.createElement(IconTip, { link: {
                name: "memory-management-maximum-memory",
                category: "hosts",
            }, iconProps: { leftMargin: true } }, "The total value in this column corresponds to the total host memory, of which a percentage is reserved buffer and not usable by SingleStore DB."))),
    sort: "DISABLED",
    formatter: (host) => {
        const renderHostMemoryUsage = ({ ratio, used, total, }) => {
            const bottomLabel = `${NumberFormatter.formatBytes(used)}/${NumberFormatter.formatBytes(total)}`;
            return (React.createElement(RatioMetricCell, { labelPosition: "top", value: ratio, bottomLabel: bottomLabel }));
        };
        return renderLoadingError(getHostMemoryUsage(host), renderHostMemoryUsage, { cell: true });
    },
    getValue: (host) => {
        const { liveMonitoring: { systemUsedMemoryB, systemTotalMemoryB }, } = host;
        if (!systemUsedMemoryB.isLoading() &&
            !systemUsedMemoryB.isError() &&
            !systemTotalMemoryB.isLoading() &&
            !systemTotalMemoryB.isError() &&
            !systemTotalMemoryB.value.isZero()) {
            return systemUsedMemoryB.value
                .dividedBy(systemTotalMemoryB.value)
                .toNumber();
        }
    },
};
export const HOST_DISK_USAGE_TIP = "Host disk usage displays the used and available disk space aggregated across all mount points being used by all SingleStore DB nodes on this host";
const HOSTS_DISK_USAGE_COLUMN = {
    id: "hostSystemDiskUsage",
    title: "SingleStore DB Disk Usage",
    sort: "DISABLED",
    description: HOST_DISK_USAGE_TIP,
    formatter: (host) => {
        const renderHostDiskUsage = ({ used, total, ratio, }) => {
            const bottomLabel = `${NumberFormatter.formatBytes(used)}/${NumberFormatter.formatBytes(total)}`;
            return (React.createElement(RatioMetricCell, { labelPosition: "top", value: ratio, bottomLabel: bottomLabel }));
        };
        return renderLoadingError(getHostDiskUsage(host), renderHostDiskUsage, {
            cell: true,
        });
    },
    getValue: (host) => {
        const { liveMonitoring: { systemUsedDiskB, systemTotalDiskB }, } = host;
        if (!systemUsedDiskB.isLoading() &&
            !systemUsedDiskB.isError() &&
            !systemTotalDiskB.isLoading() &&
            !systemTotalDiskB.isError() &&
            !systemTotalDiskB.value.isZero()) {
            return systemUsedDiskB.value
                .dividedBy(systemTotalDiskB.value)
                .toNumber();
        }
    },
};
export function getHostsColumns(physicalMonitoringEnabled) {
    if (physicalMonitoringEnabled) {
        return [
            HOSTS_ADDRESS_COLUMN,
            HOSTS_NODES_COLUMN,
            HOSTS_CPU_COUNT_COLUMN,
            HOSTS_CPU_USAGE_COLUMN,
            HOSTS_MEMORY_USAGE_COLUMN,
            HOSTS_DISK_USAGE_COLUMN,
        ];
    }
    else {
        return [
            HOSTS_ADDRESS_COLUMN,
            HOSTS_NODES_COLUMN,
            HOSTS_CPU_COUNT_COLUMN,
            HOSTS_MEMORY_COLUMN,
        ];
    }
}
export const ALL_HOSTS_COLUMNS = [
    HOSTS_ADDRESS_COLUMN,
    HOSTS_NODES_COLUMN,
    HOSTS_CPU_USAGE_COLUMN,
    HOSTS_CPU_COUNT_COLUMN,
    HOSTS_MEMORY_COLUMN,
    HOSTS_DISK_USAGE_COLUMN,
];
